import React, { useState, useRef, useEffect, useCallback, useMemo, useId } from 'react';
import Editor from '@monaco-editor/react';
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkToc from 'remark-toc';
import rehypeRaw from 'rehype-raw';
import { useTranslation } from 'react-i18next';
import {
    Bold,
    CheckSquare,
    ChevronDown,
    ChevronUp,
    Code,
    Heading1,
    Heading2,
    Heading3,
    Italic,
    Link as LinkIcon,
    List,
    ListOrdered,
    MessageCircle,
    Smile,
    Table as TableIcon,
} from "lucide-react";

import { userAPI } from '../UserAPI';

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * Marker classes for search hits. They carry no styling of their own: Monaco's
 * `inlineClassName` and `querySelectorAll` both need a *single* class token, so
 * the colours are applied from the container via the descendant utilities in
 * `SEARCH_HIGHLIGHT_STYLES` instead.
 */
const SEARCH_HIGHLIGHT = 'md-search-highlight';
const SEARCH_HIGHLIGHT_CURRENT = 'md-search-highlight-current';

/**
 * Paints the two marker classes above, for the Monaco decorations as well as
 * the `<mark>` elements of the preview. The current hit repeats its class in
 * the selector so it outranks the base hit on the preview marks, which carry
 * both classes at once.
 */
const SEARCH_HIGHLIGHT_STYLES = [
    "[&_.md-search-highlight]:bg-warning/25 [&_.md-search-highlight]:border-b [&_.md-search-highlight]:border-warning/40",
    "[&_.md-search-highlight-current.md-search-highlight-current]:bg-warning/60",
    "[&_.md-search-highlight-current.md-search-highlight-current]:border-b",
    "[&_.md-search-highlight-current.md-search-highlight-current]:border-warning",
].join(' ');

/**
 * Typography for the preview. `react-markdown` produces the HTML, so the rules
 * cannot sit on JSX elements — they are descendant utilities on the preview
 * container, replacing what the removed sass module used to do.
 */
const PREVIEW_PROSE = [
    "[&_h1]:mb-4 [&_h1]:mt-0 [&_h1]:border-b [&_h1]:border-border [&_h1]:pb-2 [&_h1]:text-[2em] [&_h1]:font-semibold",
    "[&_h2]:mb-4 [&_h2]:mt-0 [&_h2]:border-b [&_h2]:border-border [&_h2]:pb-2 [&_h2]:text-[1.5em] [&_h2]:font-semibold",
    "[&_h3]:mb-4 [&_h3]:mt-0 [&_h3]:text-[1.25em] [&_h3]:font-semibold",
    "[&_h4]:mb-4 [&_h4]:mt-0 [&_h4]:font-semibold",
    "[&_h5]:mb-4 [&_h5]:mt-0 [&_h5]:font-semibold",
    "[&_h6]:mb-4 [&_h6]:mt-0 [&_h6]:font-semibold",
    "[&_p]:mb-4 [&_p]:break-words [&_p]:leading-relaxed",
    "[&_ul]:mb-4 [&_ul]:break-words [&_ul]:pl-6",
    "[&_ol]:mb-4 [&_ol]:break-words [&_ol]:pl-6",
    "[&_li]:mb-1",
    "[&_code]:rounded-sm [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[85%]",
    "[&_pre]:mb-4 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:p-4",
    "[&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-[85%]",
    "[&_blockquote]:mb-4 [&_blockquote]:border-l-4 [&_blockquote]:border-border [&_blockquote]:px-4 [&_blockquote]:text-muted-foreground",
    "[&_table]:mb-4 [&_table]:w-full [&_table]:table-fixed [&_table]:break-words [&_table]:border-collapse",
    "[&_th]:overflow-hidden [&_th]:text-ellipsis [&_th]:border [&_th]:border-input [&_th]:bg-muted [&_th]:px-3 [&_th]:py-2 [&_th]:font-semibold",
    "[&_td]:overflow-hidden [&_td]:text-ellipsis [&_td]:border [&_td]:border-input [&_td]:px-3 [&_td]:py-2",
    "[&_a]:text-primary [&_a]:no-underline [&_a:hover]:underline",
].join(' ');

/** Compact icon buttons of the header and the toolbar. */
const TOOLBAR_BUTTON_CLASS = "h-6 w-6 shrink-0 max-sm:h-6 max-sm:w-6";
const TOOLBAR_ICON_CLASS = "h-3.5 w-3.5";

interface MarkdownEditorProps {
    value: string;
    onChange: (value: string | undefined) => void;
    height?: string;
    width?: string;
    editorOptions?: any;
    className?: string;
    placeholder?: string;
    onImageUpload?: (file: File) => Promise<{ alt: string; url: string }>;
}

export default function MarkdownEditor({
    value,
    onChange,
    height = "200px",
    width = "100%",
    editorOptions = {},
    className = "",
    placeholder,
    onImageUpload
}: MarkdownEditorProps) {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<'markdown' | 'preview'>('markdown');
    const editorRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isEditorReady, setIsEditorReady] = useState(false);
    const [editorError, setEditorError] = useState<string | null>(null);
    const [hasTextSelection, setHasTextSelection] = useState(false);
    const [showEmoticonPanel, setShowEmoticonPanel] = useState(false);
    const [emoticonSearch, setEmoticonSearch] = useState('');
    const [showTableSelector, setShowTableSelector] = useState(false);
    const [hoveredTableSize, setHoveredTableSize] = useState<{rows: number, cols: number} | null>(null);
    const [searchText, setSearchText] = useState('');
    const [searchMatches, setSearchMatches] = useState<any[]>([]);
    const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
    const monacoDecorationsRef = useRef<string[]>([]);
    const previewContainerRef = useRef<HTMLDivElement>(null);
    const fieldId = useId();
    const searchInputId = `${fieldId}-search`;
    const emoticonSearchId = `${fieldId}-emoticon-search`;

    const highlightText = useCallback((text: string) => {
        if (!searchText) return text;
        const regex = new RegExp(`(${searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        const parts = text.split(regex);
        return (
            <>
                {parts.map((part, i) => 
                    part.toLowerCase() === searchText.toLowerCase() 
                        ? <mark key={i} className={SEARCH_HIGHLIGHT}>{part}</mark> 
                        : part
                )}
            </>
        );
    }, [searchText]);

    const markdownComponents = useMemo(() => ({
        p: ({ children }: any) => <p>{React.Children.map(children, (child) => typeof child === 'string' ? highlightText(child) : child)}</p>,
        li: ({ children }: any) => <li>{React.Children.map(children, (child) => typeof child === 'string' ? highlightText(child) : child)}</li>,
        h1: ({ children }: any) => <h1>{React.Children.map(children, (child) => typeof child === 'string' ? highlightText(child) : child)}</h1>,
        h2: ({ children }: any) => <h2>{React.Children.map(children, (child) => typeof child === 'string' ? highlightText(child) : child)}</h2>,
        h3: ({ children }: any) => <h3>{React.Children.map(children, (child) => typeof child === 'string' ? highlightText(child) : child)}</h3>,
        h4: ({ children }: any) => <h4>{React.Children.map(children, (child) => typeof child === 'string' ? highlightText(child) : child)}</h4>,
        h5: ({ children }: any) => <h5>{React.Children.map(children, (child) => typeof child === 'string' ? highlightText(child) : child)}</h5>,
        h6: ({ children }: any) => <h6>{React.Children.map(children, (child) => typeof child === 'string' ? highlightText(child) : child)}</h6>,
        span: ({ children }: any) => <span>{React.Children.map(children, (child) => typeof child === 'string' ? highlightText(child) : child)}</span>,
        td: ({ children }: any) => <td>{React.Children.map(children, (child) => typeof child === 'string' ? highlightText(child) : child)}</td>,
        th: ({ children }: any) => <th>{React.Children.map(children, (child) => typeof child === 'string' ? highlightText(child) : child)}</th>,
        code: ({ children }: any) => <code>{React.Children.map(children, (child) => typeof child === 'string' ? highlightText(child) : child)}</code>,
    }), [highlightText]);

    const scrollToPreviewMatch = useCallback((index: number) => {
        if (!searchText || !previewContainerRef.current) return;
        
        const container = previewContainerRef.current;
        const marks = container.querySelectorAll(`mark.${SEARCH_HIGHLIGHT}`);
        
        if (marks.length > 0) {
            const safeIndex = index >= 0 ? index % marks.length : 0;
            const mark = marks[safeIndex] as HTMLElement;
            
            // Remove previous current highlight
            container.querySelectorAll(`.${SEARCH_HIGHLIGHT_CURRENT}`).forEach(el => {
                el.classList.remove(SEARCH_HIGHLIGHT_CURRENT);
            });
            
            // Add current highlight
            mark.classList.add(SEARCH_HIGHLIGHT_CURRENT);
            
            // Scroll to it
            mark.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [searchText]);

    // Handle search text change
    useEffect(() => {
        if (!searchText) {
            setSearchMatches([]);
            setCurrentMatchIndex(-1);
            if (editorRef.current) {
                monacoDecorationsRef.current = editorRef.current.deltaDecorations(monacoDecorationsRef.current, []);
            }
            return;
        }

        if (activeTab === 'markdown' && editorRef.current) {
            const model = editorRef.current.getModel();
            if (model) {
                const matches = model.findMatches(searchText, false, false, false, null, true);
                setSearchMatches(matches);
                if (matches.length > 0) {
                    setCurrentMatchIndex(0);
                    // Highlight all matches
                    const decorations = matches.map((match: any) => ({
                        range: match.range,
                        options: { inlineClassName: SEARCH_HIGHLIGHT }
                    }));
                    monacoDecorationsRef.current = editorRef.current.deltaDecorations(monacoDecorationsRef.current, decorations);
                    
                    // Scroll to first match
                    editorRef.current.revealRangeInCenter(matches[0].range);
                    editorRef.current.setSelection(matches[0].range);
                } else {
                    setCurrentMatchIndex(-1);
                    monacoDecorationsRef.current = editorRef.current.deltaDecorations(monacoDecorationsRef.current, []);
                }
            }
        } else if (activeTab === 'preview') {
            // Count matches in preview after render
            const timer = setTimeout(() => {
                if (previewContainerRef.current) {
                    const marks = previewContainerRef.current.querySelectorAll(`mark.${SEARCH_HIGHLIGHT}`);
                    setSearchMatches(Array.from({ length: marks.length }));
                    if (marks.length > 0) {
                        setCurrentMatchIndex(0);
                        scrollToPreviewMatch(0);
                    } else {
                        setCurrentMatchIndex(-1);
                    }
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [searchText, activeTab, isEditorReady, value, scrollToPreviewMatch]);

    const handleNextSearch = useCallback(() => {
        if (searchMatches.length === 0) return;
        const nextIndex = (currentMatchIndex + 1) % searchMatches.length;
        setCurrentMatchIndex(nextIndex);

        if (activeTab === 'markdown' && editorRef.current) {
            const match = searchMatches[nextIndex];
            editorRef.current.revealRangeInCenter(match.range);
            editorRef.current.setSelection(match.range);
            
            // Update decorations to highlight the current one differently
            const decorations = searchMatches.map((m: any, i: number) => ({
                range: m.range,
                options: { 
                    inlineClassName: i === nextIndex ? SEARCH_HIGHLIGHT_CURRENT : SEARCH_HIGHLIGHT 
                }
            }));
            monacoDecorationsRef.current = editorRef.current.deltaDecorations(monacoDecorationsRef.current, decorations);
        } else if (activeTab === 'preview') {
            scrollToPreviewMatch(nextIndex);
        }
    }, [searchMatches, currentMatchIndex, activeTab, scrollToPreviewMatch]);

    const handlePrevSearch = useCallback(() => {
        if (searchMatches.length === 0) return;
        const prevIndex = (currentMatchIndex - 1 + searchMatches.length) % searchMatches.length;
        setCurrentMatchIndex(prevIndex);

        if (activeTab === 'markdown' && editorRef.current) {
            const match = searchMatches[prevIndex];
            editorRef.current.revealRangeInCenter(match.range);
            editorRef.current.setSelection(match.range);
            
            // Update decorations to highlight the current one differently
            const decorations = searchMatches.map((m: any, i: number) => ({
                range: m.range,
                options: { 
                    inlineClassName: i === prevIndex ? SEARCH_HIGHLIGHT_CURRENT : SEARCH_HIGHLIGHT 
                }
            }));
            monacoDecorationsRef.current = editorRef.current.deltaDecorations(monacoDecorationsRef.current, decorations);
        } else if (activeTab === 'preview') {
            scrollToPreviewMatch(prevIndex);
        }
    }, [searchMatches, currentMatchIndex, activeTab, scrollToPreviewMatch]);

    // Emoticon data
    const emoticons = [
        { key: '😀', text: '😀 Grinning Face', searchTerms: ['grinning', 'face', 'happy', 'smile', 'joy'] },
        { key: '😃', text: '😃 Grinning Face with Big Eyes' },
        { key: '😄', text: '😄 Grinning Face with Smiling Eyes' },
        { key: '😁', text: '😁 Beaming Face with Smiling Eyes' },
        { key: '😆', text: '😆 Grinning Squinting Face' },
        { key: '😅', text: '😅 Grinning Face with Sweat' },
        { key: '🤣', text: '🤣 Rolling on the Floor Laughing' },
        { key: '😂', text: '😂 Face with Tears of Joy' },
        { key: '🙂', text: '🙂 Slightly Smiling Face' },
        { key: '🙃', text: '🙃 Upside-Down Face' },
        { key: '😉', text: '😉 Winking Face' },
        { key: '😊', text: '😊 Smiling Face with Smiling Eyes' },
        { key: '😇', text: '😇 Smiling Face with Halo' },
        { key: '🥰', text: '🥰 Smiling Face with Hearts' },
        { key: '😍', text: '😍 Smiling Face with Heart-Eyes' },
        { key: '🤩', text: '🤩 Star-Struck' },
        { key: '😘', text: '😘 Face Blowing a Kiss' },
        { key: '😗', text: '😗 Kissing Face' },
        { key: '😚', text: '😚 Kissing Face with Closed Eyes' },
        { key: '😙', text: '😙 Kissing Face with Smiling Eyes' },
        { key: '😋', text: '😋 Face Savoring Food' },
        { key: '😛', text: '😛 Face with Tongue' },
        { key: '😜', text: '😜 Winking Face with Tongue' },
        { key: '🤪', text: '🤪 Zany Face' },
        { key: '😝', text: '😝 Squinting Face with Tongue' },
        { key: '🤑', text: '🤑 Money-Mouth Face' },
        { key: '🤗', text: '🤗 Hugging Face' },
        { key: '🤭', text: '🤭 Face with Hand Over Mouth' },
        { key: '🤫', text: '🤫 Shushing Face' },
        { key: '🤔', text: '🤔 Thinking Face' },
        { key: '🤐', text: '🤐 Zipper-Mouth Face' },
        { key: '🤨', text: '🤨 Face with Raised Eyebrow' },
        { key: '😐', text: '😐 Neutral Face' },
        { key: '😑', text: '😑 Expressionless Face' },
        { key: '😶', text: '😶 Face Without Mouth' },
        { key: '😏', text: '😏 Smirking Face' },
        { key: '😒', text: '😒 Unamused Face' },
        { key: '🙄', text: '🙄 Face with Rolling Eyes' },
        { key: '😬', text: '😬 Grimacing Face' },
        { key: '🤥', text: '🤥 Lying Face' },
        { key: '😔', text: '😔 Pensive Face' },
        { key: '😪', text: '😪 Sleepy Face' },
        { key: '🤤', text: '🤤 Drooling Face' },
        { key: '😴', text: '😴 Sleeping Face' },
        { key: '😷', text: '😷 Face with Medical Mask' },
        { key: '🤒', text: '🤒 Face with Thermometer' },
        { key: '🤕', text: '🤕 Face with Head-Bandage' },
        { key: '🤢', text: '🤢 Nauseated Face' },
        { key: '🤮', text: '🤮 Face Vomiting' },
        { key: '🤧', text: '🤧 Sneezing Face' },
        { key: '🥵', text: '🥵 Hot Face' },
        { key: '🥶', text: '🥶 Cold Face' },
        { key: '🥴', text: '🥴 Woozy Face' },
        { key: '😵', text: '😵 Dizzy Face' },
        { key: '🤯', text: '🤯 Exploding Head' },
        { key: '🤠', text: '🤠 Cowboy Hat Face' },
        { key: '🥳', text: '🥳 Partying Face' },
        { key: '😎', text: '😎 Smiling Face with Sunglasses' },
        { key: '🤓', text: '🤓 Nerd Face' },
        { key: '🧐', text: '🧐 Face with Monocle' },
        { key: '😕', text: '😕 Confused Face' },
        { key: '😟', text: '😟 Worried Face' },
        { key: '🙁', text: '🙁 Slightly Frowning Face' },
        { key: '☹️', text: '☹️ Frowning Face' },
        { key: '😮', text: '😮 Face with Open Mouth' },
        { key: '😯', text: '😯 Hushed Face' },
        { key: '😲', text: '😲 Astonished Face' },
        { key: '😳', text: '😳 Flushed Face' },
        { key: '🥺', text: '🥺 Pleading Face' },
        { key: '😦', text: '😦 Frowning Face with Open Mouth' },
        { key: '😧', text: '😧 Anguished Face' },
        { key: '😨', text: '😨 Fearful Face' },
        { key: '😰', text: '😰 Anxious Face with Sweat' },
        { key: '😥', text: '😥 Sad but Relieved Face' },
        { key: '😢', text: '😢 Crying Face' },
        { key: '😭', text: '😭 Loudly Crying Face' },
        { key: '😱', text: '😱 Face Screaming in Fear' },
        { key: '😖', text: '😖 Confounded Face' },
        { key: '😣', text: '😣 Persevering Face' },
        { key: '😞', text: '😞 Disappointed Face' },
        { key: '😓', text: '😓 Downcast Face with Sweat' },
        { key: '😩', text: '😩 Weary Face' },
        { key: '😫', text: '😫 Tired Face' },
        { key: '🥱', text: '🥱 Yawning Face' },
        { key: '😤', text: '😤 Face with Steam From Nose' },
        { key: '😡', text: '😡 Pouting Face' },
        { key: '😠', text: '😠 Angry Face' },
        { key: '🤬', text: '🤬 Face with Symbols on Mouth' },
        { key: '😈', text: '😈 Smiling Face with Horns' },
        { key: '👿', text: '👿 Angry Face with Horns' },
        { key: '💀', text: '💀 Skull' },
        { key: '☠️', text: '☠️ Skull and Crossbones' },
        { key: '💩', text: '💩 Pile of Poo' },
        { key: '🤡', text: '🤡 Clown Face' },
        { key: '👹', text: '👹 Ogre' },
        { key: '👺', text: '👺 Goblin' },
        { key: '👻', text: '👻 Ghost' },
        { key: '👽', text: '👽 Alien' },
        { key: '👾', text: '👾 Alien Monster' },
        { key: '🤖', text: '🤖 Robot' },
        { key: '😺', text: '😺 Grinning Cat' },
        { key: '😸', text: '😸 Grinning Cat with Smiling Eyes' },
        { key: '😹', text: '😹 Cat with Tears of Joy' },
        { key: '😻', text: '😻 Smiling Cat with Heart-Eyes' },
        { key: '😼', text: '😼 Cat with Wry Smile' },
        { key: '😽', text: '😽 Kissing Cat' },
        { key: '🙀', text: '🙀 Weary Cat' },
        { key: '😿', text: '😿 Crying Cat' },
        { key: '😾', text: '😾 Pouting Cat' },
        { key: '🙈', text: '🙈 See-No-Evil Monkey' },
        { key: '🙉', text: '🙉 Hear-No-Evil Monkey' },
        { key: '🙊', text: '🙊 Speak-No-Evil Monkey' },
        { key: '💋', text: '💋 Kiss Mark' },
        { key: '👋', text: '👋 Waving Hand' },
        { key: '🤚', text: '🤚 Raised Back of Hand' },
        { key: '🖐️', text: '🖐️ Hand with Fingers Splayed' },
        { key: '✋', text: '✋ Raised Hand' },
        { key: '🖖', text: '🖖 Vulcan Salute' },
        { key: '👌', text: '👌 OK Hand' },
        { key: '🤏', text: '🤏 Pinching Hand' },
        { key: '✌️', text: '✌️ Victory Hand' },
        { key: '🤞', text: '🤞 Crossed Fingers' },
        { key: '🤟', text: '🤟 Love-You Gesture' },
        { key: '🤘', text: '🤘 Sign of the Horns' },
        { key: '🤙', text: '🤙 Call Me Hand' },
        { key: '👈', text: '👈 Backhand Index Pointing Left' },
        { key: '👉', text: '👉 Backhand Index Pointing Right' },
        { key: '👆', text: '👆 Backhand Index Pointing Up' },
        { key: '🖕', text: '🖕 Middle Finger' },
        { key: '👇', text: '👇 Backhand Index Pointing Down' },
        { key: '☝️', text: '☝️ Index Pointing Up' },
        { key: '👍', text: '👍 Thumbs Up', searchTerms: ['thumbs', 'up', 'good', 'like', 'approve', 'yes', 'positive', 'great'] },
        { key: '👎', text: '👎 Thumbs Down' },
        { key: '✊', text: '✊ Raised Fist' },
        { key: '👊', text: '👊 Oncoming Fist' },
        { key: '🤛', text: '🤛 Left-Facing Fist' },
        { key: '🤜', text: '🤜 Right-Facing Fist' },
        { key: '👏', text: '👏 Clapping Hands', searchTerms: ['clap', 'applause', 'hands', 'congratulations', 'praise', 'celebrate'] },
        { key: '🙌', text: '🙌 Raising Hands' },
        { key: '👐', text: '👐 Open Hands' },
        { key: '🤲', text: '🤲 Palms Up Together' },
        { key: '🤝', text: '🤝 Handshake' },
        { key: '🙏', text: '🙏 Folded Hands' },
        { key: '✍️', text: '✍️ Writing Hand' },
        { key: '💅', text: '💅 Nail Polish' },
        { key: '🤳', text: '🤳 Selfie' },
        { key: '💪', text: '💪 Flexed Biceps', searchTerms: ['muscle', 'strong', 'biceps', 'arm', 'flex', 'power', 'strength', 'workout', 'gym'] },
        { key: '🦾', text: '🦾 Mechanical Arm' },
        { key: '🦿', text: '🦿 Mechanical Leg' },
        { key: '🦵', text: '🦵 Leg' },
        { key: '🦶', text: '🦶 Foot' },
        { key: '👂', text: '👂 Ear' },
        { key: '🦻', text: '🦻 Ear with Hearing Aid' },
        { key: '👃', text: '👃 Nose' },
        { key: '🧠', text: '🧠 Brain' },
        { key: '🦷', text: '🦷 Tooth' },
        { key: '🦴', text: '🦴 Bone' },
        { key: '👀', text: '👀 Eyes' },
        { key: '👁️', text: '👁️ Eye' },
        { key: '👅', text: '👅 Tongue' },
        { key: '👄', text: '👄 Mouth' },
        { key: '💘', text: '💘 Heart with Arrow' },
        { key: '💝', text: '💝 Heart with Ribbon' },
        { key: '💖', text: '💖 Sparkling Heart' },
        { key: '💗', text: '💗 Growing Heart' },
        { key: '💓', text: '💓 Beating Heart' },
        { key: '💞', text: '💞 Revolving Hearts' },
        { key: '💕', text: '💕 Two Hearts' },
        { key: '💟', text: '💟 Heart Decoration' },
        { key: '❣️', text: '❣️ Heart Exclamation' },
        { key: '💔', text: '💔 Broken Heart' },
        { key: '❤️', text: '❤️ Red Heart', searchTerms: ['heart', 'love', 'red', 'romance', 'affection', 'valentine'] },
        { key: '🧡', text: '🧡 Orange Heart' },
        { key: '💛', text: '💛 Yellow Heart' },
        { key: '💚', text: '💚 Green Heart' },
        { key: '💙', text: '💙 Blue Heart' },
        { key: '💜', text: '💜 Purple Heart' },
        { key: '🖤', text: '🖤 Black Heart' },
        { key: '🤍', text: '🤍 White Heart' },
        { key: '🤎', text: '🤎 Brown Heart' },
        { key: '💯', text: '💯 Hundred Points' },
        { key: '💢', text: '💢 Anger Symbol' },
        { key: '💥', text: '💥 Collision' },
        { key: '💫', text: '💫 Dizzy' },
        { key: '💦', text: '💦 Sweat Droplets' },
        { key: '💨', text: '💨 Dashing Away' },
        { key: '🕳️', text: '🕳️ Hole' },
        { key: '💣', text: '💣 Bomb' },
        { key: '💤', text: '💤 Zzz' },
        { key: '💥', text: '💥 Collision' },
        { key: '💫', text: '💫 Dizzy' },
        { key: '💦', text: '💦 Sweat Droplets' },
        { key: '💨', text: '💨 Dashing Away' },
        { key: '🕳️', text: '🕳️ Hole' },
        { key: '💣', text: '💣 Bomb' },
        { key: '💤', text: '💤 Zzz' },
        { key: '💢', text: '💢 Anger Symbol' },
        { key: '💥', text: '💥 Collision' },
        { key: '💫', text: '💫 Dizzy' },
        { key: '💦', text: '💦 Sweat Droplets' },
        { key: '💨', text: '💨 Dashing Away' },
        { key: '🕳️', text: '🕳️ Hole' },
        { key: '💣', text: '💣 Bomb' },
        { key: '💤', text: '💤 Zzz' },
        { key: '💢', text: '💢 Anger Symbol' },
        { key: '💥', text: '💥 Collision' },
        { key: '💫', text: '💫 Dizzy' },
        { key: '💦', text: '💦 Sweat Droplets' },
        { key: '💨', text: '💨 Dashing Away' },
        { key: '🕳️', text: '🕳️ Hole' },
        { key: '💣', text: '💣 Bomb' },
        { key: '💤', text: '💤 Zzz' },
        // LLM Response Symbols
        { key: '•', text: '• Bullet Point' },
        { key: '◦', text: '◦ White Bullet' },
        { key: '▪', text: '▪ Black Small Square' },
        { key: '▫', text: '▫ White Small Square' },
        { key: '‣', text: '‣ Triangular Bullet' },
        { key: '⁃', text: '⁃ Hyphen Bullet' },
        { key: '⁌', text: '⁌ Left Bullet' },
        { key: '⁍', text: '⁍ Right Bullet' },
        { key: '✓', text: '✓ Check Mark', searchTerms: ['check', 'mark', 'done', 'complete', 'yes', 'correct', 'tick', 'approve'] },
        { key: '✔', text: '✔ Heavy Check Mark' },
        { key: '☑', text: '☑ Ballot Box with Check' },
        { key: '☐', text: '☐ Ballot Box' },
        { key: '☒', text: '☒ Ballot Box with X' },
        { key: '✗', text: '✗ Ballot X' },
        { key: '✘', text: '✘ Heavy Ballot X' },
        { key: '✕', text: '✕ Multiplication X' },
        { key: '✖', text: '✖ Heavy Multiplication X' },
        { key: '✚', text: '✚ Heavy Plus' },
        { key: '✛', text: '✛ Open Center Cross' },
        { key: '✜', text: '✜ Heavy Open Center Cross' },
        { key: '✝', text: '✝ Latin Cross' },
        { key: '✞', text: '✞ Shadowed White Latin Cross' },
        { key: '✟', text: '✟ Outlined Latin Cross' },
        { key: '✠', text: '✠ Maltese Cross' },
        { key: '✡', text: '✡ Star of David' },
        { key: '✢', text: '✢ Four Teardrop-Spoked Asterisk' },
        { key: '✣', text: '✣ Four Petal Flower' },
        { key: '✤', text: '✤ Four Spoked Asterisk' },
        { key: '✥', text: '✥ Heavy Four Spoked Asterisk' },
        { key: '✦', text: '✦ Four Pointed Black Star' },
        { key: '✧', text: '✧ Four Pointed White Star' },
        { key: '★', text: '★ Black Star' },
        { key: '☆', text: '☆ White Star' },
        { key: '✩', text: '✩ Stress Outlined White Star' },
        { key: '✪', text: '✪ Circled White Star' },
        { key: '✫', text: '✫ Open Center Black Star' },
        { key: '✬', text: '✬ Black Center White Star' },
        { key: '✭', text: '✭ Outlined Black Star' },
        { key: '✮', text: '✮ Heavy Outlined Black Star' },
        { key: '✯', text: '✯ Pinwheel Star' },
        { key: '✰', text: '✰ Shadowed White Star' },
        { key: '✱', text: '✱ Heavy Asterisk' },
        { key: '✲', text: '✲ Open Center Asterisk' },
        { key: '✳', text: '✳ Eight Spoked Asterisk' },
        { key: '✴', text: '✴ Eight Pointed Black Star' },
        { key: '✵', text: '✵ Eight Pointed Pinwheel Star' },
        { key: '✶', text: '✶ Six Pointed Black Star' },
        { key: '✷', text: '✷ Eight Pointed Rectilinear Black Star' },
        { key: '✸', text: '✸ Heavy Eight Pointed Rectilinear Black Star' },
        { key: '✹', text: '✹ Twelve Pointed Black Star' },
        { key: '✺', text: '✺ Sixteen Pointed Asterisk' },
        { key: '✻', text: '✻ Teardrop Spoked Asterisk' },
        { key: '✼', text: '✼ Open Center Teardrop Spoked Asterisk' },
        { key: '✽', text: '✽ Heavy Teardrop Spoked Asterisk' },
        { key: '✾', text: '✾ Six Petal Black and White Florette' },
        { key: '✿', text: '✿ Black Florette' },
        { key: '❀', text: '❀ White Florette' },
        { key: '❁', text: '❁ Eight Petal Outlined Black Florette' },
        { key: '❂', text: '❂ Circled Open Center Eight Pointed Star' },
        { key: '❃', text: '❃ Heavy Teardrop Spoked Pinwheel Asterisk' },
        { key: '❄', text: '❄ Snowflake' },
        { key: '❅', text: '❅ Tight Trifoliate Snowflake' },
        { key: '❆', text: '❆ Heavy Chevron Snowflake' },
        { key: '❇', text: '❇ Sparkle' },
        { key: '❈', text: '❈ Heavy Sparkle' },
        { key: '❉', text: '❉ Balloon Spoked Asterisk' },
        { key: '❊', text: '❊ Eight Spoked Propeller Asterisk' },
        { key: '❋', text: '❋ Heavy Eight Spoked Propeller Asterisk' },
        { key: '❌', text: '❌ Cross Mark' },
        { key: '❍', text: '❍ Shadowed White Circle' },
        { key: '❎', text: '❎ Negative Squared Cross Mark' },
        { key: '❏', text: '❏ Lower Right Drop Shadowed White Square' },
        { key: '❐', text: '❐ Upper Right Drop Shadowed White Square' },
        { key: '❑', text: '❑ Lower Right Shadowed White Square' },
        { key: '❒', text: '❒ Upper Right Shadowed White Square' },
        { key: '❓', text: '❓ Black Question Mark Ornament' },
        { key: '❔', text: '❔ White Question Mark Ornament' },
        { key: '❕', text: '❕ White Exclamation Mark Ornament' },
        { key: '❖', text: '❖ Black Diamond Minus White X' },
        { key: '❗', text: '❗ Heavy Exclamation Mark Symbol' },
        { key: '❘', text: '❘ Light Vertical Bar' },
        { key: '❙', text: '❙ Medium Vertical Bar' },
        { key: '❚', text: '❚ Heavy Vertical Bar' },
        { key: '❛', text: '❛ Heavy Single Turned Comma Quotation Mark Ornament' },
        { key: '❜', text: '❜ Heavy Single Comma Quotation Mark Ornament' },
        { key: '❝', text: '❝ Heavy Double Turned Comma Quotation Mark Ornament' },
        { key: '❞', text: '❞ Heavy Double Comma Quotation Mark Ornament' },
        { key: '❟', text: '❟ Heavy Low Single Comma Quotation Mark Ornament' },
        { key: '❠', text: '❠ Heavy Low Double Comma Quotation Mark Ornament' },
        { key: '❡', text: '❡ Curved Stem Paragraph Sign Ornament' },
        { key: '❢', text: '❢ Heavy Exclamation Mark Ornament' },
        { key: '❣', text: '❣ Heart Exclamation' },
        { key: '❤', text: '❤ Heavy Black Heart' },
        { key: '❥', text: '❥ Rotated Heavy Black Heart Bullet' },
        { key: '❦', text: '❦ Floral Heart Bullet' },
        { key: '❧', text: '❧ Rotated Floral Heart Bullet' },
        { key: '❨', text: '❨ Medium Left Parenthesis Ornament' },
        { key: '❩', text: '❩ Medium Right Parenthesis Ornament' },
        { key: '❪', text: '❪ Medium Flattened Left Parenthesis Ornament' },
        { key: '❫', text: '❫ Medium Flattened Right Parenthesis Ornament' },
        { key: '❬', text: '❬ Medium Left Pointing Angle Bracket Ornament' },
        { key: '❭', text: '❭ Medium Right Pointing Angle Bracket Ornament' },
        { key: '❮', text: '❮ Heavy Left Pointing Angle Quotation Mark Ornament' },
        { key: '❯', text: '❯ Heavy Right Pointing Angle Quotation Mark Ornament' },
        { key: '❰', text: '❰ Heavy Left Pointing Angle Bracket Ornament' },
        { key: '❱', text: '❱ Heavy Right Pointing Angle Bracket Ornament' },
        { key: '❲', text: '❲ Light Left Tortoise Shell Bracket Ornament' },
        { key: '❳', text: '❳ Light Right Tortoise Shell Bracket Ornament' },
        { key: '❴', text: '❴ Medium Left Curly Bracket Ornament' },
        { key: '❵', text: '❵ Medium Right Curly Bracket Ornament' },
        // Numbers and Letters
        { key: '①', text: '① Circled Digit One' },
        { key: '②', text: '② Circled Digit Two' },
        { key: '③', text: '③ Circled Digit Three' },
        { key: '④', text: '④ Circled Digit Four' },
        { key: '⑤', text: '⑤ Circled Digit Five' },
        { key: '⑥', text: '⑥ Circled Digit Six' },
        { key: '⑦', text: '⑦ Circled Digit Seven' },
        { key: '⑧', text: '⑧ Circled Digit Eight' },
        { key: '⑨', text: '⑨ Circled Digit Nine' },
        { key: '⑩', text: '⑩ Circled Number Ten' },
        { key: '⑪', text: '⑪ Circled Number Eleven' },
        { key: '⑫', text: '⑫ Circled Number Twelve' },
        { key: '⑬', text: '⑬ Circled Number Thirteen' },
        { key: '⑭', text: '⑭ Circled Number Fourteen' },
        { key: '⑮', text: '⑮ Circled Number Fifteen' },
        { key: '⑯', text: '⑯ Circled Number Sixteen' },
        { key: '⑰', text: '⑰ Circled Number Seventeen' },
        { key: '⑱', text: '⑱ Circled Number Eighteen' },
        { key: '⑲', text: '⑲ Circled Number Nineteen' },
        { key: '⑳', text: '⑳ Circled Number Twenty' },
        // Arrows
        { key: '←', text: '← Leftwards Arrow' },
        { key: '↑', text: '↑ Upwards Arrow' },
        { key: '→', text: '→ Rightwards Arrow' },
        { key: '↓', text: '↓ Downwards Arrow' },
        { key: '↔', text: '↔ Left Right Arrow' },
        { key: '↕', text: '↕ Up Down Arrow' },
        { key: '↖', text: '↖ North West Arrow' },
        { key: '↗', text: '↗ North East Arrow' },
        { key: '↘', text: '↘ South East Arrow' },
        { key: '↙', text: '↙ South West Arrow' },
        { key: '↚', text: '↚ Leftwards Arrow with Stroke' },
        { key: '↛', text: '↛ Rightwards Arrow with Stroke' },
        { key: '↜', text: '↜ Leftwards Wave Arrow' },
        { key: '↝', text: '↝ Rightwards Wave Arrow' },
        { key: '↞', text: '↞ Leftwards Two Headed Arrow' },
        { key: '↟', text: '↟ Upwards Two Headed Arrow' },
        { key: '↠', text: '↠ Rightwards Two Headed Arrow' },
        { key: '↡', text: '↡ Downwards Two Headed Arrow' },
        { key: '↢', text: '↢ Leftwards Arrow with Tail' },
        { key: '↣', text: '↣ Rightwards Arrow with Tail' },
        { key: '↤', text: '↤ Leftwards Arrow from Bar' },
        { key: '↥', text: '↥ Upwards Arrow from Bar' },
        { key: '↦', text: '↦ Rightwards Arrow from Bar' },
        { key: '↧', text: '↧ Downwards Arrow from Bar' },
        { key: '↨', text: '↨ Up Down Arrow with Base' },
        { key: '↩', text: '↩ Rightwards Arrow with Hook' },
        { key: '↪', text: '↪ Leftwards Arrow with Hook' },
        { key: '↫', text: '↫ Leftwards Arrow with Loop' },
        { key: '↬', text: '↬ Rightwards Arrow with Loop' },
        { key: '↭', text: '↭ Left Right Wave Arrow' },
        { key: '↮', text: '↮ Left Right Arrow with Stroke' },
        { key: '↯', text: '↯ Downwards Zigzag Arrow' },
        { key: '↰', text: '↰ Upwards Arrow with Tip Leftwards' },
        { key: '↱', text: '↱ Upwards Arrow with Tip Rightwards' },
        { key: '↲', text: '↲ Downwards Arrow with Tip Leftwards' },
        { key: '↳', text: '↳ Downwards Arrow with Tip Rightwards' },
        { key: '↴', text: '↴ Rightwards Arrow with Corner Downwards' },
        { key: '↵', text: '↵ Downwards Arrow with Corner Leftwards' },
        { key: '↶', text: '↶ Anticlockwise Top Semicircle Arrow' },
        { key: '↷', text: '↷ Clockwise Top Semicircle Arrow' },
        { key: '↸', text: '↸ North West Arrow to Long Bar' },
        { key: '↹', text: '↹ Leftwards Arrow to Bar over Rightwards Arrow to Bar' },
        { key: '↺', text: '↺ Anticlockwise Gapped Circle Arrow' },
        { key: '↻', text: '↻ Clockwise Gapped Circle Arrow' },
        { key: '↼', text: '↼ Leftwards Harpoon with Barb Upwards' },
        { key: '↽', text: '↽ Leftwards Harpoon with Barb Downwards' },
        { key: '↾', text: '↾ Upwards Harpoon with Barb Rightwards' },
        { key: '↿', text: '↿ Upwards Harpoon with Barb Leftwards' },
        { key: '⇀', text: '⇀ Rightwards Harpoon with Barb Upwards' },
        { key: '⇁', text: '⇁ Rightwards Harpoon with Barb Downwards' },
        { key: '⇂', text: '⇂ Downwards Harpoon with Barb Rightwards' },
        { key: '⇃', text: '⇃ Downwards Harpoon with Barb Leftwards' },
        { key: '⇄', text: '⇄ Rightwards Arrow over Leftwards Arrow' },
        { key: '⇅', text: '⇅ Upwards Arrow Leftwards of Downwards Arrow' },
        { key: '⇆', text: '⇆ Leftwards Arrow over Rightwards Arrow' },
        { key: '⇇', text: '⇇ Leftwards Paired Arrows' },
        { key: '⇈', text: '⇈ Upwards Paired Arrows' },
        { key: '⇉', text: '⇉ Rightwards Paired Arrows' },
        { key: '⇊', text: '⇊ Downwards Paired Arrows' },
        { key: '⇋', text: '⇋ Leftwards Harpoon over Rightwards Harpoon' },
        { key: '⇌', text: '⇌ Rightwards Harpoon over Leftwards Harpoon' },
        { key: '⇍', text: '⇍ Leftwards Double Arrow with Stroke' },
        { key: '⇎', text: '⇎ Left Right Double Arrow with Stroke' },
        { key: '⇏', text: '⇏ Rightwards Double Arrow with Stroke' },
        { key: '⇐', text: '⇐ Leftwards Double Arrow' },
        { key: '⇑', text: '⇑ Upwards Double Arrow' },
        { key: '⇒', text: '⇒ Rightwards Double Arrow' },
        { key: '⇓', text: '⇓ Downwards Double Arrow' },
        { key: '⇔', text: '⇔ Left Right Double Arrow' },
        { key: '⇕', text: '⇕ Up Down Double Arrow' },
        { key: '⇖', text: '⇖ North West Double Arrow' },
        { key: '⇗', text: '⇗ North East Double Arrow' },
        { key: '⇘', text: '⇘ South East Double Arrow' },
        { key: '⇙', text: '⇙ South West Double Arrow' },
        { key: '⇚', text: '⇚ Leftwards Triple Arrow' },
        { key: '⇛', text: '⇛ Rightwards Triple Arrow' },
        { key: '⇜', text: '⇜ Leftwards Squiggle Arrow' },
        { key: '⇝', text: '⇝ Rightwards Squiggle Arrow' },
        { key: '⇞', text: '⇞ Upwards Arrow with Double Stroke' },
        { key: '⇟', text: '⇟ Downwards Arrow with Double Stroke' },
        { key: '⇠', text: '⇠ Leftwards Dashed Arrow' },
        { key: '⇡', text: '⇡ Upwards Dashed Arrow' },
        { key: '⇢', text: '⇢ Rightwards Dashed Arrow' },
        { key: '⇣', text: '⇣ Downwards Dashed Arrow' },
        { key: '⇤', text: '⇤ Leftwards Arrow to Bar' },
        { key: '⇥', text: '⇥ Rightwards Arrow to Bar' },
        { key: '⇦', text: '⇦ Leftwards White Arrow' },
        { key: '⇧', text: '⇧ Upwards White Arrow' },
        { key: '⇨', text: '⇨ Rightwards White Arrow' },
        { key: '⇩', text: '⇩ Downwards White Arrow' },
        { key: '⇪', text: '⇪ Upwards White Arrow from Bar' },
        { key: '⇫', text: '⇫ Upwards White Arrow on Pedestal' },
        { key: '⇬', text: '⇬ Upwards White Arrow on Pedestal with Horizontal Bar' },
        { key: '⇭', text: '⇭ Upwards White Arrow on Pedestal with Vertical Bar' },
        { key: '⇮', text: '⇮ Upwards White Double Arrow' },
        { key: '⇯', text: '⇯ Upwards White Double Arrow on Pedestal' },
        { key: '⇰', text: '⇰ Rightwards White Arrow from Wall' },
        { key: '⇱', text: '⇱ North West Arrow to Corner' },
        { key: '⇲', text: '⇲ South East Arrow to Corner' },
        { key: '⇳', text: '⇳ Up Down White Arrow' },
        { key: '⇴', text: '⇴ Right Arrow with Small Circle' },
        { key: '⇵', text: '⇵ Downwards Arrow Leftwards of Upwards Arrow' },
        { key: '⇶', text: '⇶ Three Rightwards Arrows' },
        { key: '⇷', text: '⇷ Leftwards Arrow with Vertical Stroke' },
        { key: '⇸', text: '⇸ Rightwards Arrow with Vertical Stroke' },
        { key: '⇹', text: '⇹ Left Right Arrow with Vertical Stroke' },
        { key: '⇺', text: '⇺ Leftwards Arrow with Double Vertical Stroke' },
        { key: '⇻', text: '⇻ Rightwards Arrow with Double Vertical Stroke' },
        { key: '⇼', text: '⇼ Left Right Arrow with Double Vertical Stroke' },
        { key: '⇽', text: '⇽ Leftwards Open Headed Arrow' },
        { key: '⇾', text: '⇾ Rightwards Open Headed Arrow' },
        { key: '⇿', text: '⇿ Left Right Open Headed Arrow' }
    ];

    // Text manipulation functions
    const insertText = useCallback((before: string, after: string = '') => {
        // If editor is in error state, work with the textarea directly
        if (editorError) {
            const textarea = document.querySelector('textarea[placeholder="Enter markdown text..."]') as HTMLTextAreaElement;
            if (textarea) {
                const start = textarea.selectionStart;
                const end = textarea.selectionEnd;
                const selectedText = textarea.value.substring(start, end);
                const newText = before + selectedText + after;
                
                const newValue = textarea.value.substring(0, start) + newText + textarea.value.substring(end);
                textarea.value = newValue;
                onChange(newValue);
                
                // Set cursor position
                const newCursorPos = start + before.length + selectedText.length;
                textarea.setSelectionRange(newCursorPos, newCursorPos);
                textarea.focus();
            }
            return;
        }
        
        if (!editorRef.current) {
            return;
        }
        
        try {
            const editor = editorRef.current;
            if (!editor) {
                return;
            }
            
            // Try to get current value and selection
            let currentValue = '';
            let selection = null;
            
            try {
                if (typeof editor.getValue === 'function') {
                    currentValue = editor.getValue() || '';
                }
                if (typeof editor.getSelection === 'function') {
                    selection = editor.getSelection();
                }
            } catch (apiError) {
                // Monaco API not available, using fallback
            }
            
            // Fallback: just append to the end
            if (!selection || !editor.getModel) {
                const newValue = currentValue + before + after;
                if (typeof editor.setValue === 'function') {
                    editor.setValue(newValue);
                }
                return;
            }
            
            // Use Monaco API if available
            const model = editor.getModel();
            if (!model) {
                console.warn('No model available');
                return;
            }
            
            const selectedText = model.getValueInRange(selection) || '';
            const newText = before + selectedText + after;
            
            if (typeof editor.executeEdits === 'function') {
                editor.executeEdits('insert-text', [{
                    range: selection,
                    text: newText,
                    forceMoveMarkers: true
                }]);
                
                // Position cursor after the inserted text
                const newPosition = {
                    lineNumber: selection.startLineNumber,
                    column: selection.startColumn + before.length + selectedText.length
                };
                
                if (typeof editor.setPosition === 'function') {
                    editor.setPosition(newPosition);
                }
                if (typeof editor.focus === 'function') {
                    editor.focus();
                }
            }
        } catch (error) {
            console.error('Error inserting text:', error);
        }
    }, [isEditorReady, editorError, onChange]);

    // Line-based text manipulation for headings, quotes, lists
    const insertLinePrefix = useCallback((prefix: string) => {
        // If editor is in error state, work with the textarea directly
        if (editorError) {
            const textarea = document.querySelector('textarea[placeholder="Enter markdown text..."]') as HTMLTextAreaElement;
            if (textarea) {
                const lines = textarea.value.split('\n');
                const startLine = 0; // For textarea, we'll apply to all lines or current line
                const endLine = lines.length - 1;
                
                for (let i = startLine; i <= endLine; i++) {
                    lines[i] = prefix + lines[i];
                }
                
                const newValue = lines.join('\n');
                textarea.value = newValue;
                onChange(newValue);
                textarea.focus();
            }
            return;
        }
        
        if (!editorRef.current || !isEditorReady) {
            console.warn('Editor not ready for text insertion');
            return;
        }
        
        try {
            const editor = editorRef.current;
            if (!editor) {
                console.warn('Editor not available');
                return;
            }
            
            const selection = editor.getSelection();
            if (!selection || !editor.getModel) {
                return;
            }
            
            const model = editor.getModel();
            if (!model) {
                console.warn('No model available');
                return;
            }
            
            const startLine = selection.startLineNumber;
            const endLine = selection.endLineNumber;
            const edits = [];
            
            // Apply prefix to each selected line
            for (let lineNumber = startLine; lineNumber <= endLine; lineNumber++) {
                const lineText = model.getLineContent(lineNumber);
                const newLineText = prefix + lineText;
                
                edits.push({
                    range: {
                        startLineNumber: lineNumber,
                        startColumn: 1,
                        endLineNumber: lineNumber,
                        endColumn: lineText.length + 1
                    },
                    text: newLineText,
                    forceMoveMarkers: true
                });
            }
            
            if (edits.length > 0 && typeof editor.executeEdits === 'function') {
                editor.executeEdits('insert-line-prefix', edits);
                editor.focus();
            }
        } catch (error) {
            console.error('Error inserting line prefix:', error);
        }
    }, [isEditorReady, editorError, onChange]);

    // Toggle list styles - remove if already present, add if not
    const toggleListStyle = useCallback((prefix: string, removePattern: RegExp) => {
        if (!editorRef.current || !isEditorReady) {
            console.warn('Editor not ready for text insertion');
            return;
        }
        
        try {
            const editor = editorRef.current;
            if (!editor) {
                console.warn('Editor not available');
                return;
            }
            
            const selection = editor.getSelection();
            if (!selection || !editor.getModel) {
                return;
            }
            
            const model = editor.getModel();
            if (!model) {
                console.warn('No model available');
                return;
            }
            
            const startLine = selection.startLineNumber;
            const endLine = selection.endLineNumber;
            const edits = [];
            
            // Check if all lines already have the list style
            let allLinesHaveStyle = true;
            for (let lineNumber = startLine; lineNumber <= endLine; lineNumber++) {
                const lineText = model.getLineContent(lineNumber);
                if (!removePattern.test(lineText)) {
                    allLinesHaveStyle = false;
                    break;
                }
            }
            
            // Apply or remove prefix based on current state
            for (let lineNumber = startLine; lineNumber <= endLine; lineNumber++) {
                const lineText = model.getLineContent(lineNumber);
                let newLineText;
                
                if (allLinesHaveStyle) {
                    // Remove the list style
                    newLineText = lineText.replace(removePattern, '');
                } else {
                    // Add the list style
                    newLineText = prefix + lineText;
                }
                
                edits.push({
                    range: {
                        startLineNumber: lineNumber,
                        startColumn: 1,
                        endLineNumber: lineNumber,
                        endColumn: lineText.length + 1
                    },
                    text: newLineText,
                    forceMoveMarkers: true
                });
            }
            
            if (edits.length > 0 && typeof editor.executeEdits === 'function') {
                editor.executeEdits('toggle-list-style', edits);
                editor.focus();
            }
        } catch (error) {
            console.error('Error toggling list style:', error);
        }
    }, [isEditorReady]);

    // Check if text is selected
    const hasSelection = useCallback(() => {
        if (!editorRef.current || !isEditorReady) {
            return false;
        }
        
        try {
            const editor = editorRef.current;
            const selection = editor.getSelection();
            if (!selection) return false;
            
            return selection.startLineNumber !== selection.endLineNumber || 
                   selection.startColumn !== selection.endColumn;
        } catch (error) {
            return false;
        }
    }, [isEditorReady]);

    const insertHeading = useCallback(() => {
        setTimeout(() => insertLinePrefix('# '), 100);
    }, [insertLinePrefix]);

    const insertHeading2 = useCallback(() => {
        setTimeout(() => insertLinePrefix('## '), 100);
    }, [insertLinePrefix]);

    const insertHeading3 = useCallback(() => {
        setTimeout(() => insertLinePrefix('### '), 100);
    }, [insertLinePrefix]);

    const insertBold = useCallback(() => {
        if (hasSelection()) {
            setTimeout(() => insertText('**', '**'), 100);
        }
    }, [insertText, hasSelection]);

    const insertItalic = useCallback(() => {
        if (hasSelection()) {
            setTimeout(() => insertText('*', '*'), 100);
        }
    }, [insertText, hasSelection]);

    const insertQuote = useCallback(() => {
        setTimeout(() => insertLinePrefix('> '), 100);
    }, [insertLinePrefix]);

    const insertCode = useCallback(() => {
        setTimeout(() => insertText('`', '`'), 100);
    }, [insertText]);

    const insertLink = useCallback(() => {
        setTimeout(() => insertText('[', '](url)'), 100);
    }, [insertText]);

    const insertBulletList = useCallback(() => {
        setTimeout(() => toggleListStyle('- ', /^-\s/), 100);
    }, [toggleListStyle]);

    const insertNumberedList = useCallback(() => {
        setTimeout(() => toggleListStyle('1. ', /^\d+\.\s/), 100);
    }, [toggleListStyle]);

    const insertTaskList = useCallback(() => {
        setTimeout(() => toggleListStyle('- [ ] ', /^-\s\[[\sx]\]\s/), 100);
    }, [toggleListStyle]);

    // Filter emoticons based on search
    const filteredEmoticons = emoticons.filter(emoticon => {
        const searchLower = emoticonSearch.toLowerCase();
        return emoticon.text.toLowerCase().includes(searchLower) ||
               (emoticon.searchTerms && emoticon.searchTerms.some(term => 
                   term.toLowerCase().includes(searchLower)
               ));
    });

    // Insert emoticon
    const insertEmoticon = useCallback((emoticon: string) => {
        insertText(emoticon, '');
        setShowEmoticonPanel(false);
        setEmoticonSearch('');
    }, [insertText]);

    // Insert table function
    const insertTable = useCallback((rows: number, cols: number) => {
        let table = '';
        
        // Create header row with 5 spaces
        const headerRow = '| ' + Array(cols).fill('     ').join(' | ') + ' |';
        const separatorRow = '|' + Array(cols).fill('-----').join('|') + '|';
        
        table += headerRow + '\n';
        table += separatorRow + '\n';
        
        // Create data rows with 5 spaces
        for (let i = 0; i < rows - 1; i++) {
            const dataRow = '| ' + Array(cols).fill('     ').join(' | ') + ' |';
            table += dataRow + '\n';
        }
        
        insertText(table, '');
        setShowTableSelector(false);
    }, [insertText]);

    // Default image upload function
    const defaultImageUpload = useCallback(async (file: File): Promise<{ alt: string; url: string }> => {
        try {
            // Create FormData for file upload
            const form = new FormData();
            form.append('file', file);
            
            // Upload file using userAPI
            const result = await userAPI.uploadFile(form) as Record<string, unknown>;
            const pathOrUrl = (result.path ?? result.url ?? result.fileUrl ?? result.logoURL) as string | undefined;
            const url = pathOrUrl ? userAPI.getResourceURL(pathOrUrl) : null;
            return {
                alt: file.name,
                url: url || `placeholder://${file.name}`
            };
        } catch (error) {
            console.error('❌ Image upload failed:', error);
            
            // Return a fallback placeholder URL on error
            return {
                alt: file.name,
                url: `placeholder://${file.name}`
            };
        }
    }, []);

    // Handle drag and drop for images
    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
    }, []);

    const handleDrop = useCallback(async (e: React.DragEvent) => {
        e.preventDefault();
        
        try {
            const files = Array.from(e.dataTransfer.files);
            const imageFiles = files.filter(file => file.type.startsWith('image/'));
            
            if (imageFiles.length > 0) {
                const file = imageFiles[0];
                const uploadFunction = onImageUpload || defaultImageUpload;
                try {
                    const result = await uploadFunction(file);
                    const imageHtml = `<img src="${result.url}" style="max-width:500px">`;
                    insertText(imageHtml);
                } catch (error) {
                    console.error('Error uploading image:', error);
                    // Fallback to placeholder
                    const imageHtml = `<img src="${file.name}" style="max-width:500px">`;
                    insertText(imageHtml);
                }
            }
        } catch (error) {
            console.error('Error handling drop:', error);
        }
    }, [onImageUpload, defaultImageUpload, insertText]);

    // Handle clipboard paste for images - only for fallback textarea
    const handlePaste = useCallback(async (e: React.ClipboardEvent) => {
        // Only handle paste for fallback textarea, not Monaco editor
        if (editorRef.current) {
            return; // Let Monaco handle paste events
        }
        
        const items = Array.from(e.clipboardData.items);
        const imageItems = items.filter(item => item.type.startsWith('image/'));
        
        if (imageItems.length > 0) {
            e.preventDefault(); // Prevent default paste behavior
            
            try {
                const item = imageItems[0];
                const file = item.getAsFile();
                
                if (file) {
                    const uploadFunction = onImageUpload || defaultImageUpload;
                    try {
                        const result = await uploadFunction(file);
                        const imageHtml = `<img src="${result.url}" style="max-width:500px">`;
                        insertText(imageHtml);
                    } catch (error) {
                        console.error('Error uploading pasted image:', error);
                        // Fallback to placeholder
                        const imageHtml = `<img src="${file.name}" style="max-width:500px">`;
                        insertText(imageHtml);
                    }
                }
            } catch (error) {
                console.error('Error handling paste:', error);
            }
        }
    }, [onImageUpload, defaultImageUpload, insertText]);

    const defaultEditorOptions = {
        fontSize: 12,
        minimap: { enabled: false },
        scrollbar: {
            vertical: 'hidden' as const,
            horizontal: 'hidden' as const,
            verticalScrollbarSize: 0,
            horizontalScrollbarSize: 0,
            useShadows: false,
            verticalHasArrows: false,
            horizontalHasArrows: false
        },
        // Additional options to hide scrollbar completely
        overviewRulerLanes: 0,
        overviewRulerBorder: false,
        hideCursorInOverviewRuler: true,
        lineNumbers: 'off' as const,
        fontFamily: "SFMono-Regular, Consolas, 'Liberation Mono', Menlo, monospace",
        automaticLayout: true,
        readOnly: false,
        wordWrap: 'on' as const,
        padding: { top: 5, bottom: 5 },
        // Basic selection options that work
        selectOnLineNumbers: true,
        selectionClipboard: true,
        selectionHighlight: true,
        // Disable current line highlighting
        renderLineHighlight: 'none' as const,
        ...editorOptions
    };

    // Handle editor mount
    const handleEditorDidMount = useCallback((editor: any, monaco: any) => {
        try {
            // Clean up previous editor instance
            if (editorRef.current && editorRef.current !== editor) {
                try {
                    // Clean up global paste listener
                    if (editorRef.current._pasteCleanup) {
                        editorRef.current._pasteCleanup();
                    }
                    editorRef.current.dispose();
                } catch (disposeError) {
                    console.warn('Error disposing previous editor:', disposeError);
                }
            }
            
            setEditorError(null);
            editorRef.current = editor;
            
            // Enforce monospace font in Monaco
            try {
                if (typeof editor.updateOptions === 'function') {
                    editor.updateOptions({ 
                        fontFamily: "SFMono-Regular, Consolas, 'Liberation Mono', Menlo, monospace", 
                        fontLigatures: false,
                        automaticLayout: true // Enable automatic layout
                    });
                }
                if (monaco && monaco.editor && typeof monaco.editor.remeasureFonts === 'function') {
                    monaco.editor.remeasureFonts();
                }
            } catch {}

            // Add selection change listener
            editor.onDidChangeCursorSelection(() => {
                const selection = editor.getSelection();
                if (selection) {
                    const hasSelection = selection.startLineNumber !== selection.endLineNumber || 
                                       selection.startColumn !== selection.endColumn;
                    setHasTextSelection(hasSelection);
                }
            });
            
        // Set up Monaco editor paste handling
            
            // Override Monaco's paste command to handle images
            const originalPasteCommand = editor.getAction('editor.action.clipboardPasteAction');
            if (originalPasteCommand) {
                
                // Create a custom paste command that handles images
                editor.addAction({
                    id: 'custom-paste-with-images',
                    label: 'Paste with Image Support',
                    keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.V],
                    contextMenuGroupId: '1_modification',
                    contextMenuOrder: 1.5,
                    run: async (_editorInstance: any) => {
                        try {
                            // Get clipboard data
                            const clipboardData = await navigator.clipboard.read();
                            const imageItem = clipboardData.find(item => item.types.some(type => type.startsWith('image/')));
                            
                            if (imageItem) {
                                // Handle image paste
                                const imageType = imageItem.types.find(type => type.startsWith('image/'));
                                if (imageType) {
                                    const blob = await imageItem.getType(imageType);
                                    const file = new File([blob], 'pasted-image.png', { type: imageType });
                                    
                                    const uploadFunction = onImageUpload || defaultImageUpload;
                                    try {
                                        const result = await uploadFunction(file);
                                        const imageHtml = `<img src="${result.url}" style="max-width:500px">`;
                                        insertText(imageHtml);
                                        return;
                                    } catch (error) {
                                        console.error('Error uploading pasted image:', error);
                                        const imageHtml = `<img src="${file.name}" style="max-width:500px">`;
                                        insertText(imageHtml);
                                        return;
                                    }
                                }
                            }
                            
                            // Fallback to original paste for text
                            originalPasteCommand.run();
                        } catch (error) {
                            console.error('Error in custom paste handler:', error);
                            // Fallback to original paste
                            originalPasteCommand.run();
                        }
                    }
                });
            }
            
            // Also handle paste events on the editor container as fallback
            const editorContainer = editor.getDomNode();
            if (editorContainer) {
                const handleContainerPaste = async (e: ClipboardEvent) => {
                    const items = Array.from(e.clipboardData?.items || []);
                    const imageItems = items.filter(item => item.type.startsWith('image/'));
                
                    if (imageItems.length > 0) {
                        e.preventDefault();
                        e.stopPropagation();
                    
                        try {
                            const item = imageItems[0];
                            const file = item.getAsFile();
                            
                            if (file) {
                                const uploadFunction = onImageUpload || defaultImageUpload;
                                try {
                                    const result = await uploadFunction(file);
                                    const imageHtml = `<img src="${result.url}" style="max-width:500px">`;
                                    insertText(imageHtml);
                                } catch (error) {
                                    console.error('Error uploading pasted image:', error);
                                    const imageHtml = `<img src="${file.name}" style="max-width:500px">`;
                                    insertText(imageHtml);
                                }
                            }
                        } catch (error) {
                            console.error('Error handling container paste:', error);
                        }
                    }
                };
                
                editorContainer.addEventListener('paste', handleContainerPaste, true);
            
            // Store cleanup function
            editor._pasteCleanup = () => {
                editorContainer.removeEventListener('paste', handleContainerPaste, true);
            };
            }
            
            // Add a global paste listener to handle images when Monaco editor is focused
            const globalPasteListener = async (e: ClipboardEvent) => {
                // Check if the paste is happening in the Monaco editor
                const editorContainer = editor.getDomNode();
                if (editorContainer && editorContainer.contains(e.target as Node)) {
                    const items = Array.from(e.clipboardData?.items || []);
                const imageItems = items.filter(item => item.type.startsWith('image/'));
                
                if (imageItems.length > 0) {
                    e.preventDefault();
                    e.stopPropagation();
                    
                    try {
                        const item = imageItems[0];
                        const file = item.getAsFile();
                        
                        if (file) {
                            const uploadFunction = onImageUpload || defaultImageUpload;
                                try {
                                    const result = await uploadFunction(file);
                                const imageHtml = `<img src="${result.url}" style="max-width:500px">`;
                                insertText(imageHtml);
                                } catch (error) {
                                console.error('Error uploading pasted image:', error);
                                const imageHtml = `<img src="${file.name}" style="max-width:500px">`;
                                insertText(imageHtml);
                                }
                        }
                    } catch (error) {
                            console.error('Error handling global paste:', error);
                        }
                    }
                }
            };
            document.addEventListener('paste', globalPasteListener, true);
            
            // Store cleanup function
            const originalCleanup = editor._pasteCleanup;
            editor._pasteCleanup = () => {
                if (originalCleanup) originalCleanup();
                document.removeEventListener('paste', globalPasteListener, true);
            };
            
            // Set ready state immediately after mount
            setIsEditorReady(true);
            setEditorError(null); // Clear any previous errors
        
        // Set the value explicitly after mount
        if (value !== undefined) {
            editor.setValue(value || '');
        }
        
        // Manual layout trigger after a short delay
        setTimeout(() => {
                if (editorRef.current === editor) {
                editorRef.current.layout();
            }
        }, 100);
        } catch (error) {
            console.error('Error mounting editor:', error);
            setEditorError('Failed to initialize editor');
        }
    }, [value, onImageUpload, defaultImageUpload, insertText]);

    // Add timeout to detect editor loading failure
    useEffect(() => {
        // Only set timeout if editor is not ready and we're on markdown tab
        if (!isEditorReady && activeTab === 'markdown') {
            const timeout = setTimeout(() => {
                console.warn('Editor failed to load within timeout');
                setEditorError('Editor failed to load');
            }, 15000); // 15 second timeout

            return () => clearTimeout(timeout);
        }
    }, [isEditorReady, activeTab]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (editorRef.current && editorRef.current._pasteCleanup) {
                editorRef.current._pasteCleanup();
            }
        };
    }, []);

    // Add global error handler for script errors
    useEffect(() => {
        const handleGlobalError = (event: ErrorEvent) => {
            if (event.message === 'Script error.' || event.filename?.includes('monaco')) {
                console.warn('Monaco Editor script error detected, switching to fallback');
                setEditorError('Editor script error - using fallback');
                event.preventDefault();
                return false;
            }
        };

        window.addEventListener('error', handleGlobalError);
        return () => window.removeEventListener('error', handleGlobalError);
    }, []);


    // Handle resize with debouncing
    useEffect(() => {
        if (!isEditorReady || !editorRef.current) return;

        const handleResize = () => {
            if (editorRef.current) {
                editorRef.current.layout();
            }
        };

        // Debounced resize handler
        let timeoutId: NodeJS.Timeout;
        const debouncedResize = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(handleResize, 150);
        };

        window.addEventListener('resize', debouncedResize);
        
        return () => {
            window.removeEventListener('resize', debouncedResize);
            clearTimeout(timeoutId);
        };
    }, [isEditorReady]);

    // Update editor value when prop changes
    useEffect(() => {
        if (editorRef.current && isEditorReady) {
            const currentValue = editorRef.current.getValue();
            if (currentValue !== value) {
                editorRef.current.setValue(value || '');
            }
        }
    }, [value, isEditorReady]);

    // Clean up on unmount
    useEffect(() => {
        return () => {
            if (editorRef.current) {
                try {
                editorRef.current.dispose();
                } catch (error) {
                    console.warn('Error disposing editor on unmount:', error);
                }
                editorRef.current = null;
            }
            setIsEditorReady(false);
            setEditorError(null);
        };
    }, []);


    return (
        <div
            className={cn(
                "flex w-full min-w-0 max-w-full flex-col overflow-hidden rounded-md border border-input",
                className
            )}
            style={{ width, height, minHeight: height, minWidth: 0 }}
        >
            <div className="relative flex min-h-8 min-w-0 items-center gap-1 overflow-visible border-b border-input bg-muted">
            <div className="flex min-w-0 flex-none">
                <button
                    type="button"
                    className={cn(
                        "min-h-6 min-w-6 border-b-2 border-transparent px-1.5 py-1 text-sm text-muted-foreground hover:bg-accent",
                        activeTab === 'markdown' && "border-primary bg-background text-foreground"
                    )}
                    onClick={() => setActiveTab('markdown')}
                >
                    {t('ui.markdown')}
                </button>
                <button
                    type="button"
                    className={cn(
                        "min-h-6 min-w-6 border-b-2 border-transparent px-1.5 py-1 text-sm text-muted-foreground hover:bg-accent",
                        activeTab === 'preview' && "border-primary bg-background text-foreground"
                    )}
                    onClick={() => setActiveTab('preview')}
                >
                    {t('ui.preview')}
                </button>
            </div>
            <div className="flex h-full min-w-[56px] flex-1 items-center gap-0.5 bg-background px-0.5">
                {/* The header is a 32px strip — a visible label would not fit, so the
                    field is named for assistive technology only. */}
                <Label htmlFor={searchInputId} className="sr-only">{t('ui.search')}</Label>
                <Input
                    id={searchInputId}
                    className="h-6 min-w-[56px] max-w-[100px] rounded-none border-0 bg-transparent px-1 text-xs shadow-none focus-visible:ring-0 max-sm:h-6"
                    placeholder={t('ui.search') || "Search..."}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value || '')}
                    autoComplete="off"
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            if (e.shiftKey) {
                                handlePrevSearch();
                            } else {
                                handleNextSearch();
                            }
                        }
                    }}
                />
                {searchText && (
                    <div className="flex h-full min-w-7 items-center justify-center p-1 text-xs text-muted-foreground">
                        {searchMatches.length > 0 ? `${currentMatchIndex + 1}/${searchMatches.length}` : '0/0'}
                    </div>
                )}
                <div className="flex">
                    <Button
                        variant="ghost"
                        size="icon"
                        className={TOOLBAR_BUTTON_CLASS}
                        onClick={handlePrevSearch}
                        disabled={searchMatches.length === 0}
                        title="Previous Match (Shift+Enter)"
                        aria-label="Previous Match (Shift+Enter)"
                    >
                        <ChevronUp className={TOOLBAR_ICON_CLASS} />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className={TOOLBAR_BUTTON_CLASS}
                        onClick={handleNextSearch}
                        disabled={searchMatches.length === 0}
                        title="Next Match (Enter)"
                        aria-label="Next Match (Enter)"
                    >
                        <ChevronDown className={TOOLBAR_ICON_CLASS} />
                    </Button>
                </div>
            </div>
            <TooltipProvider>
            <div
                className="flex min-h-8 shrink-0 grow-0 flex-wrap items-center gap-0.5 bg-background px-1.5 py-1"
                style={{ visibility: activeTab === 'markdown' ? 'visible' : 'hidden' }}
            >
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className={TOOLBAR_BUTTON_CLASS}
                                        onClick={insertHeading}
                                        aria-label={t('ui.markdownToolbar.heading')}
                                    >
                                        <Heading1 className={TOOLBAR_ICON_CLASS} />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>{t('ui.markdownToolbar.heading')}</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className={TOOLBAR_BUTTON_CLASS}
                                        onClick={insertHeading2}
                                        aria-label={t('ui.markdownToolbar.heading2')}
                                    >
                                        <Heading2 className={TOOLBAR_ICON_CLASS} />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>{t('ui.markdownToolbar.heading2')}</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className={TOOLBAR_BUTTON_CLASS}
                                        onClick={insertHeading3}
                                        aria-label={t('ui.markdownToolbar.heading3')}
                                    >
                                        <Heading3 className={TOOLBAR_ICON_CLASS} />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>{t('ui.markdownToolbar.heading3')}</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className={TOOLBAR_BUTTON_CLASS}
                                        onClick={insertBold}
                                        disabled={!hasTextSelection}
                                        aria-label={t('ui.markdownToolbar.bold')}
                                    >
                                        <Bold className={TOOLBAR_ICON_CLASS} />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>{t('ui.markdownToolbar.bold')}</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className={TOOLBAR_BUTTON_CLASS}
                                        onClick={insertItalic}
                                        disabled={!hasTextSelection}
                                        aria-label={t('ui.markdownToolbar.italic')}
                                    >
                                        <Italic className={TOOLBAR_ICON_CLASS} />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>{t('ui.markdownToolbar.italic')}</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className={TOOLBAR_BUTTON_CLASS}
                                        onClick={insertQuote}
                                        aria-label={t('ui.markdownToolbar.quote')}
                                    >
                                        <MessageCircle className={TOOLBAR_ICON_CLASS} />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>{t('ui.markdownToolbar.quote')}</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className={TOOLBAR_BUTTON_CLASS}
                                        onClick={insertCode}
                                        aria-label={t('ui.markdownToolbar.code')}
                                    >
                                        <Code className={TOOLBAR_ICON_CLASS} />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>{t('ui.markdownToolbar.code')}</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className={TOOLBAR_BUTTON_CLASS}
                                        onClick={insertLink}
                                        aria-label={t('ui.markdownToolbar.link')}
                                    >
                                        <LinkIcon className={TOOLBAR_ICON_CLASS} />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>{t('ui.markdownToolbar.link')}</TooltipContent>
                            </Tooltip>
                            <div></div>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className={TOOLBAR_BUTTON_CLASS}
                                        onClick={insertBulletList}
                                        aria-label={t('ui.markdownToolbar.bullet')}
                                    >
                                        <List className={TOOLBAR_ICON_CLASS} />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>{t('ui.markdownToolbar.bullet')}</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className={TOOLBAR_BUTTON_CLASS}
                                        onClick={insertNumberedList}
                                        aria-label={t('ui.markdownToolbar.numbered')}
                                    >
                                        <ListOrdered className={TOOLBAR_ICON_CLASS} />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>{t('ui.markdownToolbar.numbered')}</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className={TOOLBAR_BUTTON_CLASS}
                                        onClick={insertTaskList}
                                        aria-label={t('ui.markdownToolbar.task')}
                                    >
                                        <CheckSquare className={TOOLBAR_ICON_CLASS} />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>{t('ui.markdownToolbar.task')}</TooltipContent>
                            </Tooltip>
                            <div></div>

                            {/* Emoticon picker. Anchored by the popover trigger itself, which
                                replaces the button ref the callout needed. */}
                            <Popover
                                open={showEmoticonPanel}
                                onOpenChange={(open) => {
                                    setShowEmoticonPanel(open);
                                    if (!open) {
                                        setEmoticonSearch('');
                                    }
                                }}
                            >
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className={TOOLBAR_BUTTON_CLASS}
                                                aria-label={t('ui.markdownToolbar.emoticon')}
                                            >
                                                <Smile className={TOOLBAR_ICON_CLASS} />
                                            </Button>
                                        </PopoverTrigger>
                                    </TooltipTrigger>
                                    <TooltipContent>{t('ui.markdownToolbar.emoticon')}</TooltipContent>
                                </Tooltip>
                                <PopoverContent align="start" className="w-auto p-2">
                                    <div className="mb-2 text-sm font-semibold">
                                        Choose an Emoticon
                                    </div>
                                    <Label htmlFor={emoticonSearchId} className="sr-only">{t('ui.search')}</Label>
                                    <Input
                                        id={emoticonSearchId}
                                        className="mb-3"
                                        placeholder="Search emoticons..."
                                        value={emoticonSearch}
                                        onChange={(e) => setEmoticonSearch(e.target.value || '')}
                                    />
                                    <div className="grid max-h-[300px] grid-cols-8 gap-0.5 overflow-y-auto py-1">
                                        {filteredEmoticons.map((emoticon, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                onClick={() => insertEmoticon(emoticon.key)}
                                                className="flex h-8 w-8 items-center justify-center rounded-sm p-0 text-base transition-transform hover:scale-110 hover:bg-accent"
                                                title={emoticon.text}
                                            >
                                                {emoticon.key}
                                            </button>
                                        ))}
                                    </div>
                                    {filteredEmoticons.length === 0 && (
                                        <div className="p-5 text-center text-muted-foreground">
                                            No emoticons found matching "{emoticonSearch}"
                                        </div>
                                    )}
                                </PopoverContent>
                            </Popover>

                            {/* Table size picker - Microsoft Style */}
                            <Popover
                                open={showTableSelector}
                                onOpenChange={(open) => {
                                    setShowTableSelector(open);
                                    if (!open) {
                                        setHoveredTableSize(null);
                                    }
                                }}
                            >
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className={TOOLBAR_BUTTON_CLASS}
                                                aria-label={t('ui.markdownToolbar.table')}
                                            >
                                                <TableIcon className={TOOLBAR_ICON_CLASS} />
                                            </Button>
                                        </PopoverTrigger>
                                    </TooltipTrigger>
                                    <TooltipContent>{t('ui.markdownToolbar.table')}</TooltipContent>
                                </Tooltip>
                                <PopoverContent align="start" className="w-auto p-2">
                                    <div className="mb-2 text-sm font-semibold">
                                        Insert Table
                                    </div>
                                    <div className="grid h-[120px] w-[200px] grid-cols-10 gap-px border border-input bg-muted">
                                        {Array.from({ length: 100 }, (_, i) => {
                                            const row = Math.floor(i / 10) + 1;
                                            const col = (i % 10) + 1;
                                            const isHovered = hoveredTableSize &&
                                                row <= hoveredTableSize.rows &&
                                                col <= hoveredTableSize.cols;

                                            return (
                                                <div
                                                    key={i}
                                                    onMouseEnter={() => setHoveredTableSize({ rows: row, cols: col })}
                                                    onClick={() => {
                                                        insertTable(row, col);
                                                        setShowTableSelector(false);
                                                        setHoveredTableSize(null);
                                                    }}
                                                    className={cn(
                                                        "h-[18px] w-[18px] cursor-pointer border border-border transition-colors",
                                                        isHovered ? "bg-primary" : "bg-background"
                                                    )}
                                                />
                                            );
                                        })}
                                    </div>
                                    <div className="mt-2 text-center text-xs text-muted-foreground">
                                        {hoveredTableSize ?
                                            `${hoveredTableSize.rows} × ${hoveredTableSize.cols} table` :
                                            'Drag to select table size'
                                        }
                                    </div>
                                </PopoverContent>
                            </Popover>
                </div>
                </TooltipProvider>
            </div>
            <div className={cn("relative min-h-0 w-full flex-1 overflow-hidden", SEARCH_HIGHLIGHT_STYLES)}>
                {!value && placeholder && (
                    <div className="pointer-events-none absolute z-10 whitespace-pre-wrap text-muted-foreground" style={{
                        fontFamily: activeTab === 'markdown' ? (editorOptions.fontFamily || defaultEditorOptions.fontFamily) : "inherit",
                        fontSize: activeTab === 'markdown' ? (editorOptions.fontSize || defaultEditorOptions.fontSize) : "14px",
                        top: activeTab === 'markdown' ? "6px" : "16px",
                        left: activeTab === 'markdown' ? "10px" : "16px"
                    }}>
                        {placeholder}
                    </div>
                )}
                <div
                    ref={containerRef}
                    className="absolute inset-0 h-full w-full min-w-0 overflow-hidden"
                    style={{
                        visibility: activeTab === 'markdown' ? 'visible' : 'hidden',
                        pointerEvents: activeTab === 'markdown' ? 'auto' : 'none'
                    }}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onPaste={handlePaste}
                >
                    {editorError ? (
                        <div className="flex h-full flex-col items-center justify-center border border-input bg-muted p-4 text-muted-foreground">
                            <p>Editor failed to load. Using fallback textarea.</p>
                            <p>Error: {editorError}</p>
                            <p>Editor Ready: {isEditorReady ? 'Yes' : 'No'}</p>
                            <textarea
                                value={value || ''}
                                onChange={(e) => onChange(e.target.value)}
                                className="h-[calc(100%-40px)] w-full resize-none border border-input p-2 font-mono text-xs"
                                placeholder="Enter markdown text..."
                            />
                        </div>
                    ) : (
                        <Editor
                            height="100%"
                            width="100%"
                            defaultLanguage="markdown"
                            options={defaultEditorOptions}
                            defaultValue={value || ''}
                            value={value || ''}
                            onChange={onChange}
                            onMount={handleEditorDidMount}
                            theme="vs"
                        />
                    )}
                </div>

                <div
                    ref={previewContainerRef}
                    className={cn(
                        "relative block h-full w-full min-w-0 overflow-auto bg-background p-4",
                        PREVIEW_PROSE
                    )}
                    style={{
                        visibility: activeTab === 'preview' ? 'visible' : 'hidden',
                        pointerEvents: activeTab === 'preview' ? 'auto' : 'none'
                    }}
                >
                    <Markdown 
                        rehypePlugins={[rehypeRaw]} 
                        remarkPlugins={[remarkGfm, [remarkToc, { heading: 'toc|inhaltsverzeichnis|inhalt|table[ -]of[ -]contents?' }]]}
                        components={markdownComponents as any}
                    >
                        {value || ''}
                    </Markdown>
                </div>
            </div>
        </div>
    );
}
